/**
 * useKeystoneChatSession — the Keystone-chat session state machine (U8).
 *
 * Owns the wiring the @assistant-ui runtime and the shared Composer can't:
 *
 *   - **Lazy create (decision #2):** `startNew()` makes NO API call — it just
 *     clears the picker slate. The conversation is created on the FIRST
 *     `handleSend`, passing the picked `personaId`, then the new id is committed
 *     to the parent (which drives the `DurableStreamRuntimeProvider` mount) and
 *     the verbatim message is queued (`pendingFirstMessage`) for the provider to
 *     flush once it has connected.
 *   - **Persona binding:** the picked persona is held locally for the start-only
 *     chip and the create `personaId`. The persona LOCK (hiding the picker once a
 *     conversation has ≥1 message) is enforced by the surface swap + Composer
 *     gating in `KeystoneChatSurface`, which derives `started = messageCount > 0`
 *     from the live stream — NOT by this hook.
 *   - **Reset on context switch:** changing account OR conversation clears the
 *     persona, the queued message, and any create error.
 *
 * The persona NAME for the reload chip is sourced separately by the parent from
 * the loaded conversation's `personaName` (decision #3) — this hook only carries
 * the picked persona for the start-only chip and the create `personaId`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { z } from 'zod';
import { CreateConversationResponseSchema } from '@clay-run/keystone-contract';
import type { PersonaListItem } from '../../../types/personas';

/** The two create-response fields the chat session reads. `personaName` is
 *  present only when the persona resolved server-side. */
export const KeystoneCreateResultSchema = CreateConversationResponseSchema.pick({
  conversationId: true,
  personaName: true,
});
export type KeystoneCreateResult = z.infer<typeof KeystoneCreateResultSchema>;

/**
 * The synchronous claim/release + state-mutation surface `runLazyCreate`
 * needs from its caller. A setters-only port would reintroduce the await
 * race the in-flight guard exists to prevent — `tryBeginCreate`/`endCreate`
 * MUST be synchronous (ref-backed), not derived from React state.
 */
export interface LazyCreatePort {
  /** Synchronously claim the in-flight create slot. Returns `false` (and
   *  performs no other side effects) when a create is already in progress —
   *  this is the queue-or-drop double-create policy. */
  tryBeginCreate: () => boolean;
  /** Release the in-flight slot. Always called exactly once per successful
   *  `tryBeginCreate`, in a `finally` — success, stale-discard, or error. */
  endCreate: () => void;
  setPendingFirstMessage: (text: string | null) => void;
  setCreateError: (message: string | null) => void;
  commitConversationId: (id: string) => void;
}

export interface RunLazyCreateArgs {
  text: string;
  /** Builds the create POST body. Called once, synchronously, right after
   *  `tryBeginCreate` succeeds — snapshot any values (e.g. account id) that
   *  must not drift for the lifetime of this create here. */
  buildBody: () => { selectedAccountId: string; personaId?: string };
  /** Create a Keystone conversation. Called with a single argument when
   *  `signal` is omitted (preserves V4's exact call arity — no `undefined`
   *  second argument), or with `(body, signal)` when a signal is supplied. */
  createConversation: (
    body: { selectedAccountId: string; personaId?: string },
    signal?: AbortSignal,
  ) => Promise<KeystoneCreateResult>;
  /** Caller-owned abort signal (e.g. for a "stop" action). Absent for V4. */
  signal?: AbortSignal;
  /** Checked once, after the create POST resolves. `true` means a generation
   *  or account switch happened while the request was in flight — the result
   *  is silently discarded (no error, no commit) so it doesn't corrupt a
   *  newer session's state. */
  isStale: () => boolean;
  port: LazyCreatePort;
}

/**
 * The lazy-create routine (U2 extraction): trim-guard → synchronous
 * claim-or-drop → build body → POST → staleness check → commit-or-discard →
 * release. Both `useKeystoneChatSession` (V4) and the Flow sessions store
 * (U3) run this same routine against their own port — see
 * `plans/flow-chat-multisession/coding-standards.md` §6 ("Forking the
 * runtime" / "Shared single-slot flags").
 */
export async function runLazyCreate({
  text,
  buildBody,
  createConversation,
  signal,
  isStale,
  port,
}: RunLazyCreateArgs): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return;

  // Synchronous in-flight guard (queue-or-drop policy). Drop the second call
  // entirely — never build its body, never POST, never queue its text.
  if (!port.tryBeginCreate()) return;

  try {
    const body = buildBody();
    const result = signal
      ? await createConversation(body, signal)
      : await createConversation(body);

    // Stale discard: a generation/account switch happened while the POST was
    // in flight. The orphaned Keystone conversation row is acceptable;
    // corrupted client state is not. Silent — no error surfaced.
    if (isStale()) return;

    // Queue the verbatim (trimmed) message BEFORE committing the id, so a
    // remounted stream provider sees it.
    port.setPendingFirstMessage(trimmed);
    port.commitConversationId(result.conversationId);
  } catch (err) {
    // Caller-initiated abort (e.g. "stop") is a silent discard, never an
    // error toast/banner. A timeout or any other rejection is a real error.
    if (signal?.aborted) return;
    port.setPendingFirstMessage(null);
    port.setCreateError(err instanceof Error ? err.message : 'Failed to create conversation');
  } finally {
    // Always release the claim — success, stale-discard, abort, or error.
    port.endCreate();
  }
}

export interface UseKeystoneChatSessionArgs {
  accountId: string;
  /** The currently selected conversation id, or null when none is open. */
  conversationId: string | null;
  /** Create a Keystone conversation. Throws on failure (the message bubbles to
   *  `createError`). When `personaId` is omitted the body is byte-identical to
   *  the pre-U8 no-persona create (back-compat). */
  createConversation: (body: {
    selectedAccountId: string;
    personaId?: string;
  }) => Promise<KeystoneCreateResult>;
  /** Commit the freshly-created conversation id to the parent (sets params). */
  setConversationId: (id: string) => void;
}

export interface UseKeystoneChatSessionReturn {
  /** The locally-picked persona (start-only chip). Null when none picked. */
  persona: PersonaListItem | null;
  /** The verbatim first message queued for the provider to send on mount. */
  pendingFirstMessage: string | null;
  /** Non-null when the lazy create failed (surfaces the existing error UI). */
  createError: string | null;
  /**
   * True while a lazy conversation-create is in flight. PreChatSurface should
   * disable the Composer send button when this is true so the user cannot
   * accidentally queue a double-create.
   */
  isCreating: boolean;
  /** Pick the start persona. */
  pickPersona: (p: PersonaListItem) => void;
  /** Clear the picker slate to start a new chat — NO API call (lazy create). */
  startNew: () => void;
  /**
   * Send a message. When there is no open conversation, lazily creates one
   * (passing the picked personaId), commits the new id, and queues the verbatim
   * message. When a conversation is already open, returns the text so the caller
   * can hand it to the live stream's `send`.
   *
   * If a create is already in flight, the second call is dropped (queue-or-drop
   * policy: one create, one pending message). The caller must not call this
   * again until `isCreating` returns to false.
   */
  handleSend: (text: string) => Promise<void>;
  /** Pull the queued first message exactly once (returns null after). */
  consumePendingFirstMessage: () => string | null;
  /** Clear a surfaced create error (e.g. on retry). */
  clearCreateError: () => void;
}

export function useKeystoneChatSession({
  accountId,
  conversationId,
  createConversation,
  setConversationId,
}: UseKeystoneChatSessionArgs): UseKeystoneChatSessionReturn {
  const [persona, setPersona] = useState<PersonaListItem | null>(null);
  const [pendingFirstMessage, setPendingFirstMessage] = useState<string | null>(
    null,
  );
  const [createError, setCreateError] = useState<string | null>(null);
  // Exposed as `isCreating` so PreChatSurface can disable the Composer while
  // the create is in flight (prevents the user from firing a second create
  // before the first has settled).
  const [isCreating, setIsCreating] = useState(false);
  // Ref mirror: read from inside async callbacks without re-creating handleSend.
  const isCreatingRef = useRef(false);

  // The conversation id this hook just committed via lazy create. The reset
  // effect must NOT treat the lazy-create transition (null → newId, which the
  // hook itself initiated on first send) as a user-initiated context switch —
  // doing so would clobber the queued `pendingFirstMessage` and the picked
  // persona right after create. Set in `handleSend`, consumed by the effect.
  const justCommittedIdRef = useRef<string | null>(null);

  // ---- Reset on context switch (account OR conversation) -------------------
  // Switching accounts/conversations clears the picked persona, the queued
  // first message, and any create error (spec edge case). Keyed on both so
  // selecting a different conversation
  // in the same account resets just as a full account switch does. We skip the
  // mount run — initial state is already clean, and resetting on mount would
  // clobber a persona picked synchronously in the same act/commit — AND we skip
  // the hook's own lazy-create commit (null → the id we just created), which is
  // a continuation of the same session, not a switch.
  const prevContextRef = useRef<string | null>(null);
  useEffect(() => {
    const ctx = `${accountId}::${conversationId ?? ''}`;
    if (prevContextRef.current === null) {
      prevContextRef.current = ctx;
      return;
    }
    if (prevContextRef.current === ctx) return;

    // Lazy-create commit: the conversation we just created is now the open id.
    // Adopt the new context WITHOUT resetting — the persona + queued first
    // message must survive into the freshly-mounted stream.
    if (conversationId && justCommittedIdRef.current === conversationId) {
      justCommittedIdRef.current = null;
      prevContextRef.current = ctx;
      return;
    }

    prevContextRef.current = ctx;
    setPersona(null);
    setPendingFirstMessage(null);
    setCreateError(null);
  }, [accountId, conversationId]);

  const pickPersona = useCallback((p: PersonaListItem) => {
    // Persona is pickable pre-chat; the lock once a conversation exists is
    // enforced by the surface swap + Composer gating in KeystoneChatSurface,
    // not here (the hook never sees the live message count).
    setPersona(p);
  }, []);

  const startNew = useCallback(() => {
    // Lazy create (decision #2): no API call here. The first send creates.
    setPersona(null);
    setPendingFirstMessage(null);
    setCreateError(null);
  }, []);

  // Latest persona without re-creating handleSend on every pick.
  const personaRef = useRef(persona);
  personaRef.current = persona;
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const pendingFirstMessageRef = useRef(pendingFirstMessage);
  pendingFirstMessageRef.current = pendingFirstMessage;

  // Ref for the accountId in flight so async callbacks can detect stale results
  // without needing accountId in handleSend's dependency array (which would
  // re-create the callback on every account switch).
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;

  const handleSend = useCallback(
    async (text: string) => {
      // Existing conversation: nothing to create — the caller hands the text to
      // the live stream's `send` (the provider is already mounted).
      if (conversationIdRef.current) return;

      // Snapshot the accountId used for THIS create. Set inside `buildBody`
      // (called by the routine right after it claims the in-flight slot) and
      // read back by `isStale` after the POST resolves — same moment the
      // pre-extraction code snapshotted it, just relayed through the routine's
      // callback seam instead of a single inline closure.
      let createAccountId = accountIdRef.current;

      await runLazyCreate({
        text,
        buildBody: () => {
          createAccountId = accountIdRef.current;
          // Lazy create on first send. Conditionally include personaId so a
          // no-persona create forwards a byte-identical `{ selectedAccountId }`
          // body (back-compat with the pre-U8 create).
          const pickedPersona = personaRef.current;
          return pickedPersona
            ? { selectedAccountId: createAccountId, personaId: pickedPersona.id }
            : { selectedAccountId: createAccountId };
        },
        createConversation,
        // V4 never supplies a signal — `runLazyCreate` calls `createConversation`
        // with exactly one argument in that case (back-compat call arity).
        isStale: () => accountIdRef.current !== createAccountId,
        port: {
          tryBeginCreate: () => {
            // In-flight guard (double-send protection). Drop the second
            // submit; the first create is already in progress.
            if (isCreatingRef.current) return false;
            isCreatingRef.current = true;
            setIsCreating(true);
            return true;
          },
          endCreate: () => {
            isCreatingRef.current = false;
            setIsCreating(false);
          },
          setPendingFirstMessage: (msg) => setPendingFirstMessage(msg),
          setCreateError: (err) => setCreateError(err),
          commitConversationId: (id) => {
            // Mark this id as our own commit so the reset effect adopts it
            // without wiping the persona + queued message (it's a
            // continuation, not a switch).
            justCommittedIdRef.current = id;
            setConversationId(id);
          },
        },
      });
    },
    [createConversation, setConversationId],
  );

  const consumePendingFirstMessage = useCallback(() => {
    const msg = pendingFirstMessageRef.current;
    pendingFirstMessageRef.current = null;
    setPendingFirstMessage(null);
    return msg;
  }, []);

  const clearCreateError = useCallback(() => setCreateError(null), []);

  return {
    persona,
    pendingFirstMessage,
    createError,
    isCreating,
    pickPersona,
    startNew,
    handleSend,
    consumePendingFirstMessage,
    clearCreateError,
  };
}
